<!-- desktop template -->
<div *appIsDesktop class="section-container" [formGroup]="priceAsFormGroup">
  <h2 class="price-as-title">{{ appStrings.quoteDetails_PriceAs }}</h2>
  <div class="fields-container">
    <ng-container *ngTemplateOutlet="price; context: { listPrice, netPrice }"></ng-container>
    <ng-container *ngTemplateOutlet="priceAsInputs; context: { priceAsFormGroup }"></ng-container>
    <button id="addToQuoteButton" mat-button class="button primary add-quote-button half-width" (click)="addToQuote()">
      {{ appStrings.products_addToQuote }}
    </button>
    <button id="startNewQuoteButton" mat-button class="button mat-button half-width" (click)="startNewQuote()"
      id="startNewQuote">
      {{ appStrings.products_startNewQuote }}
    </button>
  </div>
</div>

<!-- mobile template -->
@if (isLoading) {
  <div class="overlay full" id="productDetails-loadingSpinner">
    <p class="text-center">{{ appStrings.pleaseWait }}</p>
    <mat-spinner id="productsLoader"></mat-spinner>
  </div>
}

<div *appIsMobile class="section-container mobile" [formGroup]="priceAsFormGroup">
  <div class="fields-container">
    <div class="price-container">
      <ng-container *ngTemplateOutlet="price; context: { listPrice, netPrice }"></ng-container>
    </div>
    <div class="input-container">
      <ng-container *ngTemplateOutlet="priceAsInputs; context: { priceAsFormGroup }"></ng-container>
    </div>
    <div class="buttons-container">
      <button id="startNewQuoteButton" mat-button class="button mat-button half-width" (click)="startNewQuote()"
        id="startNewQuote">
        {{ appStrings.products_startNewQuote }}
      </button>
      <button id="addToQuoteButton" mat-button class="button primary add-quote-button half-width" (click)="addToQuote()">
        {{ appStrings.products_addToQuote }}
      </button>
    </div>
  </div>
</div>

<!-- common -->
<ng-template #price let-listPrice="listPrice" let-netPrice="netPrice">
  <div class="price-field">
    <span id="listPriceLabel" class="price-label">{{ appStrings.customTool_listPriceLabel }}</span>
    @if (listPrice) {
      <span id="listPriceValue" class="list-price">{{ listPrice | currency: currencySymbol }}</span>
    }
    @if (!listPrice) {
      <span id="listPriceNoValue" class="list-price">{{ appStrings.notApplicable }}</span>
    }
  </div>
  <div class="price-field">
    <span id="netPriceLabel" class="price-label">{{ appStrings.products_netPrice }}</span>
    @if (hasNetPrice) {
      <span id="netPriceValue" class="net-price">{{ netPrice | currency: currencySymbol }}</span>
    }
    @if (!hasNetPrice) {
      <span id="netPriceNoValue" class="net-price">{{ appStrings.notApplicable }}</span>
    }
  </div>
</ng-template>

<ng-template #priceAsInputs let-priceAsFormGroup="priceAsFormGroup">
  <div [formGroup]="priceAsFormGroup">

    <!-- Account Manager input -->
    <mat-form-field appearance="outline" class="paragraph-text" [ngClass]="{'has-error': amValidationMessage}">
      <mat-label id="accountManagerLabel">{{ appStrings.quoteDetails_AccountManager }}</mat-label>
      <input id="quoteDetailsAccountManagerSelect" #accountManagerAutocomplete="matAutocompleteTrigger"
        #accountManagerSelect formControlName="accountManager" matInput [matAutocomplete]="amSelector"
        (input)="filterAccountManagerList($event.target.value)" (focus)="$event.target.select()"
        (blur)="checkIfSelectedAccountManager($event)"/>
        <mat-autocomplete #amSelector="matAutocomplete" (optionSelected)="onAccountManagerSelect($event)"
          [displayWith]="displayAccountManager">
          @for (aManager of filteredAccountManagerList; track aManager) {
            <mat-option [value]="aManager">
              <span [ngClass]="{'red':aManager.terminated}">{{ aManager.name }}</span>
            </mat-option>
          }
          @if (filteredAccountManagerList && !filteredAccountManagerList.length) {
            <mat-option>
              {{ appStrings.noResults }}
            </mat-option>
          }
        </mat-autocomplete>
        @if (amValidationMessage) {
          <span class="error-text">{{ amValidationMessage }}</span>
        }
      </mat-form-field>

      <!-- Customer input -->
      <mat-form-field appearance="outline" class="paragraph-text" [ngClass]="{'has-error': customerValidationMessage}">
        <mat-label id="customerLabel">{{ appStrings.quoteDetails_Customer }}</mat-label>
        <input id="quoteDetailsCustomerSelect" #customerAutocomplete="matAutocompleteTrigger" #customerSelect
          formControlName="customer" matInput [matAutocomplete]="customerSelector"
          (input)="filterCustomerList($event.target.value)" (focus)="$event.target.select()"/>
          <mat-autocomplete #customerSelector="matAutocomplete" (optionSelected)="onCustomerSelect($event)"
            [displayWith]="displayCustomer"  class="customer-autocomplete">
            @for (customer of filteredCustomerList; track customer) {
              <mat-option class="mat-option-large" [value]="customer">
                @if (customer) {
                  <div class="customer-option-title customer-option-title-long">@if (customer.isLead) {
                    <span id="quoteLeadTypeIcon"
                    class="crm-industrial-font icon-lead top-icon"></span>
                  }{{ customer.name }}</div>
                  @if (customer.childName1) {
                    <div class="customer-option-subtitle">
                      {{ customer.childName1 }}
                    </div>
                  }
                  <div class="customer-option-details">
                    {{ customer.address }}
                  </div>
                  @if (!customer.isLead) {
                    <div class="customer-option-subtitle">
                      {{ appStrings.quote_bpLabel }}:{{customer.bpNumber}}
                    </div>
                  }
                  @if (customer.isLead) {
                    <div class="customer-option-subtitle">
                      {{ appStrings.quote_bpStatusLabel }}:{{leadStatusCodeList[customer.status]}}
                    </div>
                  }
                  @if (!customer.isLead) {
                    <div class="customer-option-subtitle">
                      {{ appStrings.quote_bpStatusLabel }}:{{lnStatusCodeList[customer.status]}}
                    </div>
                  }
                }
              </mat-option>
            }
            @if (filteredCustomerList && !filteredCustomerList.length) {
              <mat-option class="small">
                {{ appStrings.noResults }}
              </mat-option>
            }
          </mat-autocomplete>
          @if (customerValidationMessage) {
            <span class="error-text">{{ customerValidationMessage }}</span>
          }
        </mat-form-field>

        <!-- Price Matrix input -->
        <mat-form-field appearance="outline" class="paragraph-text half-width" [ngClass]="{'has-error': priceMatrixValidationMessage}">
          <mat-label id="priceMatrixLabel">{{ appStrings.quoteDetails_PriceMatrix }}</mat-label>
          <input id="priceMatrixSelect" #priceMatrixSelect="matAutocompleteTrigger" formControlName="priceMatrix" matInput
            maxlength="3" appSpecialCharactersValidations [dashAllowed]="false" [matAutocomplete]="priceMatrixSelector"
            (input)="filterPriceMatrixList($event.target.value)" (focus)="$event.target.select()" #priceMatrixInput/>
            <mat-autocomplete #priceMatrixSelector="matAutocomplete" (optionSelected)="onPriceMatrixSelect($event)"
              [displayWith]="displayPriceMatrix"  >
              @for (pMatrix of filteredPriceMatrixList; track pMatrix) {
                <mat-option [value]="pMatrix">
                  <span>{{ pMatrix.priceList }}</span>
                </mat-option>
              }
              @if (filteredPriceMatrixList && !filteredPriceMatrixList.length) {
                <mat-option>
                  {{ appStrings.noResults }}
                </mat-option>
              }
            </mat-autocomplete>
            @if (priceMatrixValidationMessage) {
              <span class="error-text">{{ priceMatrixValidationMessage }}</span>
            }
          </mat-form-field>

          <!-- Quantity input -->
          <mat-form-field appearance="outline" class="paragraph-text half-width">
            <mat-label id="quantityLabel">{{ appStrings.products_gridQuantity }}</mat-label>
            <input type="number" id="quantityEditInput" matInput formControlName="quantity" class="small text-right"
              appDigitsOnly maxlength="5" />
            </mat-form-field>
          </div>
        </ng-template>